import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const AnimalInsect: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/AnimalInsect.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/AnimalInsect.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Common Insects – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn about common insects like ants, bees, butterflies, and mosquitoes in Class 3 Science. Explore their features, habitats, and importance with engaging content at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1 className="mb-5">Chapter 4: Common Insects</h1>

                            <section>
                                <h2><span className="emoji">🐞</span>&nbsp;Common Insects Around Us</h2>
                                <p>Insects are small animals found almost everywhere. Here are some you might see every day:</p>
                                <ul>
                                    <li><strong>Ants</strong> – Tiny crawlers that love sweets.</li>
                                    <li><strong>Bees</strong> – Fuzzy insects that make honey.</li>
                                    <li><strong>Butterflies</strong> – Colorful insects that fly around flowers.</li>
                                    <li><strong>Mosquitoes</strong> – Small flying insects that bite.</li>
                                    <li><strong>Houseflies</strong> – Insects that sit on food and spread germs.</li>
                                    <li><strong>Cockroaches</strong> – Often seen in dark, damp places.</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🐜</span>&nbsp;Body Parts of an Insect</h2>
                                <p>All insects have three main body parts:</p>
                                <ul>
                                    <li><strong>Head</strong> – Has eyes, mouth, and antennae.</li>
                                    <li><strong>Thorax</strong> – The middle part with legs and wings.</li>
                                    <li><strong>Abdomen</strong> – The end part with internal organs.</li>
                                </ul>
                                <p>Insects also have:</p>
                                <ul>
                                    <li><strong>Two antennae</strong> – To sense things.</li>
                                    <li><strong>Six legs</strong> – Three pairs.</li>
                                    <li><strong>Some have wings</strong> – Like bees and butterflies.</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">😟</span>&nbsp;Harmful Effects of Insects</h2>
                                <p>Some insects can be harmful to our health and homes:</p>
                                <ul>
                                    <li><strong>Mosquitoes</strong> – Can spread diseases like malaria.</li>
                                    <li><strong>Houseflies</strong> – Spread germs and make food dirty.</li>
                                    <li><strong>Termites</strong> – Damage wood.</li>
                                    <li><strong>Bedbugs</strong> – Cause itching and bites.</li>
                                </ul>
                                <h3>✅ Prevention Tips:</h3>
                                <ul>
                                    <li>Keep your home clean and dry.</li>
                                    <li>Use mosquito nets and repellents.</li>
                                    <li>Cover all food.</li>
                                    <li>Don’t let water collect in containers.</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🌼</span>&nbsp;Benefits of Insects</h2>
                                <p>Many insects are helpful to us and nature:</p>
                                <ul>
                                    <li><strong>Bees</strong> – Make honey and help plants grow.</li>
                                    <li><strong>Butterflies</strong> – Help in pollination.</li>
                                    <li><strong>Ladybugs</strong> – Eat harmful insects in gardens.</li>
                                    <li><strong>Silkworms</strong> – Produce silk for clothes.</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🦋</span>&nbsp;Life Cycle of a Butterfly</h2>
                                <p>A butterfly goes through <strong>four stages</strong> in its life:</p>
                                <ul>
                                    <li><strong>Egg</strong> – Laid on leaves.</li>
                                    <li><strong>Larva (Caterpillar)</strong> – Hatches and eats a lot.</li>
                                    <li><strong>Pupa (Chrysalis)</strong> – Rests inside a shell.</li>
                                    <li><strong>Adult Butterfly</strong> – Comes out and flies away.</li>
                                </ul>
                                <p>This complete change is called <strong>metamorphosis</strong>.</p>
                            </section>

                            <section className="summary">
                                <h2><span className="emoji">🌟</span>&nbsp;Summary</h2>
                                <ul>
                                    <li>Insects have three body parts and six legs.</li>
                                    <li>Some insects are harmful, but many are helpful.</li>
                                    <li>Butterflies and bees help plants grow.</li>
                                    <li>We should keep our surroundings clean to stay safe from harmful insects.</li>
                                    <li>A butterfly’s life cycle has four stages.</li>
                                </ul>
                            </section>

                            <section className="activity">
                                <h2><span className="emoji">📚</span>&nbsp;Fun Activity</h2>
                                <p><strong>Option 1:</strong> Draw and label the body parts of an insect (like a bee or ant).</p>
                                <p><strong>Option 2:</strong> Make a chart showing the life cycle of a butterfly using pictures or clay models.</p>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🐝 Class 3 Science – Chapter 4: Common Insects</h1>

                        <section>
                            <h2><span className="emoji">🐞</span>Common Insects Around Us</h2>
                            <p>Insects are small animals found almost everywhere. Here are some you might see every day:</p>
                            <ul>
                                <li><strong>Ants</strong> – Tiny crawlers that love sweets.</li>
                                <li><strong>Bees</strong> – Fuzzy insects that make honey.</li>
                                <li><strong>Butterflies</strong> – Colorful insects that fly around flowers.</li>
                                <li><strong>Mosquitoes</strong> – Small flying insects that bite.</li>
                                <li><strong>Houseflies</strong> – Insects that sit on food and spread germs.</li>
                                <li><strong>Cockroaches</strong> – Often seen in dark, damp places.</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🐜</span>Body Parts of an Insect</h2>
                            <p>All insects have three main body parts:</p>
                            <ul>
                                <li><strong>Head</strong> – Has eyes, mouth, and antennae.</li>
                                <li><strong>Thorax</strong> – The middle part with legs and wings.</li>
                                <li><strong>Abdomen</strong> – The end part with internal organs.</li>
                            </ul>
                            <p>Insects also have:</p>
                            <ul>
                                <li><strong>Two antennae</strong> – To sense things.</li>
                                <li><strong>Six legs</strong> – Three pairs.</li>
                                <li><strong>Some have wings</strong> – Like bees and butterflies.</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">😟</span>Harmful Effects of Insects</h2>
                            <p>Some insects can be harmful to our health and homes:</p>
                            <ul>
                                <li><strong>Mosquitoes</strong> – Can spread diseases like malaria.</li>
                                <li><strong>Houseflies</strong> – Spread germs and make food dirty.</li>
                                <li><strong>Termites</strong> – Damage wood.</li>
                                <li><strong>Bedbugs</strong> – Cause itching and bites.</li>
                            </ul>
                            <h3>✅ Prevention Tips:</h3>
                            <ul>
                                <li>Keep your home clean and dry.</li>
                                <li>Use mosquito nets and repellents.</li>
                                <li>Cover all food.</li>
                                <li>Don’t let water collect in containers.</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🌼</span>Benefits of Insects</h2>
                            <p>Many insects are helpful to us and nature:</p>
                            <ul>
                                <li><strong>Bees</strong> – Make honey and help plants grow.</li>
                                <li><strong>Butterflies</strong> – Help in pollination.</li>
                                <li><strong>Ladybugs</strong> – Eat harmful insects in gardens.</li>
                                <li><strong>Silkworms</strong> – Produce silk for clothes.</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🦋</span>Life Cycle of a Butterfly</h2>
                            <p>A butterfly goes through <strong>four stages</strong> in its life:</p>
                            <ul>
                                <li><strong>Egg</strong> – Laid on leaves.</li>
                                <li><strong>Larva (Caterpillar)</strong> – Hatches and eats a lot.</li>
                                <li><strong>Pupa (Chrysalis)</strong> – Rests inside a shell.</li>
                                <li><strong>Adult Butterfly</strong> – Comes out and flies away.</li>
                            </ul>
                            <p>This complete change is called <strong>metamorphosis</strong>.</p>
                        </section>

                        <section className="summary">
                            <h2><span className="emoji">🌟</span>Summary</h2>
                            <ul>
                                <li>Insects have three body parts and six legs.</li>
                                <li>Some insects are harmful, but many are helpful.</li>
                                <li>Butterflies and bees help plants grow.</li>
                                <li>We should keep our surroundings clean to stay safe from harmful insects.</li>
                                <li>A butterfly’s life cycle has four stages.</li>
                            </ul>
                        </section>

                        <section className="activity">
                            <h2><span className="emoji">📚</span>Fun Activity</h2>
                            <p><strong>Option 1:</strong> Draw and label the body parts of an insect (like a bee or ant).</p>
                            <p><strong>Option 2:</strong> Make a chart showing the life cycle of a butterfly using pictures or clay models.</p>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default AnimalInsect;
